import React, { useEffect, useState } from 'react';
import {
  sanitizeNumericDraft,
  formatEmptyableNumber,
  resolveEmptyableCommit,
} from '../utils/parseEmptyableNumber';

/**
 * Number field that can stay empty after the user clears it, instead of
 * snapping back to 0 (which makes the next digit land as "02").
 */
export default function EmptyableNumberInput({
  value,
  onChange,
  integer = false,
  allowEmpty = true,
  commitOnChange = true,
  min,
  max,
  className,
  disabled,
  onFocus,
  onBlur,
  ...props
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => formatEmptyableNumber(value, { integer }));

  useEffect(() => {
    if (!focused) setDraft(formatEmptyableNumber(value, { integer }));
  }, [value, integer, focused]);

  const display = focused ? draft : formatEmptyableNumber(value, { integer });

  return (
    <input
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      autoComplete="off"
      value={display}
      disabled={disabled}
      className={className}
      onFocus={(event) => {
        setFocused(true);
        setDraft(formatEmptyableNumber(value, { integer }));
        event.target.select();
        onFocus?.(event);
      }}
      onChange={(event) => {
        const nextDraft = sanitizeNumericDraft(event.target.value, { integer });
        setDraft(nextDraft);
        if (!commitOnChange) return;
        const result = resolveEmptyableCommit(nextDraft, {
          integer,
          allowEmpty,
          min,
          max,
          clamp: false,
        });
        if (result.ok) onChange(result.value);
      }}
      onBlur={(event) => {
        setFocused(false);
        const result = resolveEmptyableCommit(draft, {
          integer,
          allowEmpty,
          min,
          max,
          clamp: true,
        });
        if (result.ok) {
          onChange(result.value);
          setDraft(formatEmptyableNumber(result.value, { integer }));
        } else {
          setDraft(formatEmptyableNumber(value, { integer }));
        }
        onBlur?.(event);
      }}
      {...props}
    />
  );
}
